import { Stack, Space, Divider, AppShell, Group } from "@mantine/core";
import {
  useActiveUser,
  useActiveUserPartner,
  useDrawer,
  useIsLoggedIn,
  useShowPartnerToggle,
} from "@/store/selectors";
import { Drawer } from "@/components/ui/Drawer";
import { NavLink } from "@/components/ui/NavLink";
import { Link } from "@/components/ui/Link";
import { useStore } from "@/store";
import { config } from "@/config";
import { Analytics } from "@/services/Analytics";
import { Button } from "@/components/ui/Button";
import { ActionIcon } from "@/components/ui/ActionIcon";
import { Navigation } from "@/services/Navigation";
import {
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTiktok,
} from "@tabler/icons-react";
import { useNavItems } from "@/store/selectors/uiSelectors";
import { Text } from "../ui/Text";

export const drawerId = "mainMenuDrawer";

export function MainMenuDrawer() {
  const { closeDrawer } = useDrawer(drawerId);
  const activeUser = useActiveUser();
  const activeUserPartner = useActiveUserPartner();
  const setActiveFilterPartnerId = useStore(
    (state) => state.advizerSearch.setActiveFilterPartnerId,
  );
  const NAV_ITEMS = useNavItems();
  const showPartnerToggle = useShowPartnerToggle();
  const isLoggedIn = useIsLoggedIn();
  const socialBtnSize = "xl";
  const socialBtnVariant = "filled";
  const socialIconSize = 28;

  return (
    <Drawer id={drawerId} style={{ zIndex: 1000 }} position="left" size="md">
      <AppShell.Navbar p="md" w="100%" bg="background.1">
        <Stack h="100%" justify="space-between" p="md" w="100%">
          <Stack>
            <Space h="md" />
            <Stack gap={0}>
              {NAV_ITEMS.map((navItem, index) => {
                const hide = navItem.hide?.(
                  activeUserPartner?.id,
                  showPartnerToggle,
                );
                if (hide) return null;

                if (!navItem.label) {
                  return <Divider key={index} mt="md" mb="sm" />;
                }

                return (
                  <NavLink
                    key={index}
                    to={navItem.href}
                    onClick={(event) => {
                      if (navItem.onClick) {
                        navItem.onClick({
                          partnerId: activeUserPartner?.id,
                        });
                      } else if (navItem.label === "Alum Advizers") {
                        setActiveFilterPartnerId(activeUserPartner?.id);
                      } else if (navItem.label === "Explore Advizers") {
                        setActiveFilterPartnerId(null);
                      }

                      Analytics.click({
                        action: `Menu Drawer > ${navItem.label}`,
                        event,
                      });

                      if (navItem.dontClose) {
                        return;
                      }
                      closeDrawer();
                    }}
                    Icon={navItem.Icon}
                    CustomIcon={navItem.CustomIcon?.(activeUserPartner?.id)}
                    linkProps={{ target: navItem.target }}
                  >
                    {typeof navItem.label === "function"
                      ? navItem.label({ isPartnerApp: config.isPartnerApp, partner: activeUserPartner })
                      : navItem.label}
                  </NavLink>
                );
              })}
            </Stack>

            {!isLoggedIn && (
              <Link
                to="/login"
                onClick={(event) => {
                  Analytics.click({
                    action: "Go to sign in from drawer",
                    event,
                  });
                  closeDrawer();
                }}
              >
                <Button fullWidth radius="xl" size="lg" variant="light">
                  Login / Sign up
                </Button>
              </Link>
            )}

            <Space h="md" />
            <Group justify="center" align="center">
              <ActionIcon
                ariaLabel="Go to LinkedIn"
                variant={socialBtnVariant}
                size={socialBtnSize}
                onClick={() =>
                  Navigation.openExternalLink(config.socialLinks.linkedin)
                }
              >
                <IconBrandLinkedin size={socialIconSize} />
              </ActionIcon>
              <ActionIcon
                ariaLabel="Go to TikTok"
                variant={socialBtnVariant}
                size={socialBtnSize}
                onClick={() =>
                  Navigation.openExternalLink(config.socialLinks.tiktok)
                }
                color="black"
              >
                <IconBrandTiktok size={socialIconSize} color="white" />
              </ActionIcon>
              <ActionIcon
                ariaLabel="Go to Instagram"
                variant={socialBtnVariant}
                size={socialBtnSize}
                onClick={() =>
                  Navigation.openExternalLink(config.socialLinks.instagram)
                }
                color="pink"
              >
                <IconBrandInstagram size={32} color="pink" />
              </ActionIcon>
            </Group>
            <Space h="sm" />
            <Text c="dimmed" size="sm" w="100%" ta="center">
              {activeUser?.email || "Anonymous"}
            </Text>
          </Stack>
          <Group gap="xs">
            {[
              {
                label: "Terms",
                href: config.termsUrl,
                onClick: () => {
                  Analytics.click({
                    action: "Main Drawer > Terms",
                  });
                },
              },
              {
                label: "Privacy",
                href: config.privacyUrl,
                onClick: () => {
                  Analytics.click({
                    action: "Main Drawer > Privacy",
                  });
                },
              },
              {
                label: "Accessibility",
                href: config.accessibilityUrl,
                onClick: () => {
                  Analytics.click({
                    action: "Main Drawer > Accessibility",
                  });
                },
              },
            ].map((item) => {
              return (
                <Link
                  to={item.href}
                  key={item.label}
                  target="_blank"
                  style={{ fontSize: 12 }}
                  onClick={item.onClick}
                >
                  {item.label}
                </Link>
              );
            })}
          </Group>
        </Stack>
      </AppShell.Navbar>
    </Drawer>
  );
}
